import React, { FC, useContext, useEffect, useState } from 'react'
import { PurchaseOrderParcelDeliveryInput } from '../type/purchase-order'
import { Button, Card, Checkbox, Col, DatePicker, Flex, Form, Modal, Row, Space } from 'antd'
import { translate, translateTemplate } from '../../../core'
import { dateFormat, toMoment } from '../../../core/until'
import { Item } from '../type'
import { NumericInput } from '../../../core/component'
import { SaveOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { PurchaseOrderCreateParcelDelivery, PurchaseOrderQuery } from '../graphql/purchase-order.graphql'
import Enumerable from 'linq'
import classNames from 'classnames'
import { PurchaseOrderListItemContext } from './PurchaseOrderListItemContext'
import styles from './PurchaseOrderListItemParcelEntryForm.module.scss'

export type  PurchaseOrderListItemParcelEntryFormProps = {
  onClose: () => void
}

export type PurchaseOrderParcelDeliveryEntry = Pick<PurchaseOrderParcelDeliveryInput, 'date'> & {
  items: {
    item: Item
    description: string
    quantity: number
    costPrice: number
    remaining: number
    ordered: number
    isActive: boolean
  }[]
}

// column widths shared by the header row and every item row so they line up
const gap = 16
const checkboxWidth = 32
const skuWidth = 120
const remainingWidth = 110
const costWidth = 170
const quantityWidth = 190
const dateWidth = 200
const controlHeight = 48

export const PurchaseOrderListItemParcelEntryForm: FC<PurchaseOrderListItemParcelEntryFormProps> = ({onClose}) => {
  const {message, entity} = useContext(PurchaseOrderListItemContext)
  const [form] = Form.useForm<PurchaseOrderParcelDeliveryEntry>()

  useEffect(() => {
    form.setFieldsValue({
      date: toMoment(new Date()),
      items: Enumerable.from(entity.items)
        .groupBy(
          e => e.item.id,
          e => e,
          (_, items) => ({
            item: items.first().item,
            description: items.select(it => it.description).firstOrDefault(it => !!it)
              ?? items.first().item.description,
            originalQuantity: items.sum(it => it.quantity),
            quantity: items.sum(it => it.quantity - it.quantityReceived),
            price: items.first().price,
          }),
        )
        .select(it => ({
          item: it.item,
          description: it.description,
          quantity: it.quantity,
          costPrice: it.price,
          remaining: it.quantity,
          ordered: it.originalQuantity,
          isActive: false,
        }))
        .where(it => it.quantity > 0)
        .toArray()
    })
  }, [])

  const [isSaved, setIsSaved] = useState<boolean>(false)

  const [create, {loading: isSending}] = useMutation(PurchaseOrderCreateParcelDelivery, {
    refetchQueries: [PurchaseOrderQuery],
    onCompleted: () => {
      setIsSaved(true)
      message.success(translate('parcel_delivery', 'save_success_message'))
    },
    onError: error => message.error(error.message),
  })

  return (
    <Card className='w-100 border mt-3'>
      <Form
        onFinish={data => create({
          variables: {
            input: {
              purchaseOrderId: entity.id,
              date: data.date,
              items: data.items
                .filter(it => it.isActive)
                .map(it => ({
                  itemId: it.item.id,
                  quantity: it.quantity,
                  costPrice: it.costPrice,
                })),
            }
          }
        })}
        form={form}
        layout='vertical'
      >
        <Row>
          <Col>
            <span className='fs-5'>{translate('new', 'delivery')}</span>
          </Col>
        </Row>

        <Row className='mt-3'>
          <Col>
            <div className='text-muted f-medium mb-1'>{translate('date')}</div>
            <Form.Item name='date' className={styles.field}>
              <DatePicker format={dateFormat} style={{width: dateWidth}}/>
            </Form.Item>
          </Col>
        </Row>

        <Row className='mt-4'>
          <Col span={24}>
            <Flex align='center' gap={gap} className='text-muted f-medium px-2 pb-2 border-bottom'>
              <div style={{width: checkboxWidth}}/>
              <div style={{flex: 1, minWidth: 0}}>{translate('product')}</div>
              <div className={styles.detailColumn} style={{width: skuWidth}}>{translate('sku')}</div>
              <div className={styles.detailColumn} style={{width: remainingWidth}}>{translate('remaining')}</div>
              <div style={{width: costWidth}}>
                <span className='text-danger me-1'>*</span>{translate('cost_price')}
              </div>
              <div style={{width: quantityWidth}}>
                <span className='text-danger me-1'>*</span>{translate('quantity', 'received')}
              </div>
            </Flex>

            <Form.List name='items'>
              {(fields) =>
                fields.map(({key, name}, index) => (
                  <Form.Item
                    key={key}
                    noStyle
                    shouldUpdate
                  >
                    {({getFieldValue}) => {
                      const row = getFieldValue(['items', name]) as PurchaseOrderParcelDeliveryEntry['items'][number]
                      const isActive = getFieldValue(['items', name, 'isActive']) as boolean
                      const remaining = (
                        <span>
                          <span className='fw-semibold'>{row.remaining}</span>{' '}
                          <span className='text-muted'>
                            {translateTemplate('remaining_of_ordered', {ordered: row.ordered})}
                          </span>
                        </span>
                      )

                      return (
                        <Flex
                          align='start'
                          gap={gap}
                          className={classNames('py-3 px-2', index ? 'border-top' : '', isActive && styles.activeRow)}
                        >
                          <Flex align='center' style={{width: checkboxWidth, height: controlHeight}}>
                            <Form.Item
                              name={[name, 'isActive']}
                              valuePropName='checked'
                              className={classNames('mb-0', styles.field)}
                            >
                              <Checkbox/>
                            </Form.Item>
                          </Flex>

                          <Flex
                            vertical
                            justify='center'
                            className='f-medium'
                            style={{flex: 1, minWidth: 0, minHeight: controlHeight}}
                          >
                            <div className='fw-semibold'>{row.description ?? row.item.description}</div>
                            <div className={classNames('text-break', styles.compactDetails)}>
                              {translate('sku')}: {row.item.sku} | {translate('remaining')} {remaining}
                            </div>
                          </Flex>

                          <Flex
                            align='center'
                            className={classNames('f-medium text-break', styles.detailColumn)}
                            style={{width: skuWidth, minHeight: controlHeight}}
                          >
                            {row.item.sku}
                          </Flex>

                          <Flex
                            align='center'
                            className={classNames('f-medium', styles.detailColumn)}
                            style={{width: remainingWidth, minHeight: controlHeight}}
                          >
                            {remaining}
                          </Flex>

                          <div style={{width: costWidth}}>
                            <Form.Item
                              name={[name, 'costPrice']}
                              className={classNames('mb-0 w-100', styles.field)}
                              rules={[
                                {
                                  required: true,
                                  message: translate('cost_price', 'is_required'),
                                },
                              ]}
                            >
                              <NumericInput
                                disabled={!isActive}
                                decimalPlaces={2}
                              />
                            </Form.Item>
                          </div>

                          <div style={{width: quantityWidth}}>
                            <Form.Item
                              name={[name, 'quantity']}
                              className={classNames('mb-0 w-100', styles.field)}
                              rules={[
                                {
                                  required: true,
                                  message: translate('quantity', 'is_required'),
                                },
                                form => ({
                                  validator: (_, value, callback) => {
                                    const remaining = form.getFieldValue(['items', name, 'remaining'])

                                    if (value !== undefined && value !== null && value <= 0)
                                      return callback(translate('quantity', 'must_be_greater_than_zero'))

                                    callback(
                                      value > remaining
                                        ? translate('quantity', 'is_greater_than', remaining)
                                        : undefined
                                    )
                                  },
                                })
                              ]}
                            >
                              <NumericInput
                                disabled={!isActive}
                                decimalPlaces={0}
                              />
                            </Form.Item>
                          </div>
                        </Flex>
                      )
                    }}
                  </Form.Item>
                ))
              }
            </Form.List>
          </Col>
        </Row>
        <Space className='mt-4'>
          <Form.Item
            shouldUpdate
            noStyle
          >
            {({getFieldValue}) => {
              const activeItems = ((getFieldValue('items') ?? []) as PurchaseOrderParcelDeliveryEntry['items'])
                .filter(it => it.isActive)

              return <Button
                disabled={!activeItems.length}
                loading={isSending || isSaved}
                onClick={() => form.validateFields()
                  .then(() => Modal.confirm({
                    title: translate('parcel_delivery'),
                    content: translate('sure_message'),
                    onOk: form.submit,
                  }))
                  .catch(() => undefined)}
                type='primary'
                icon={<SaveOutlined/>}
              >
                {translate('save')}
              </Button>
            }}
          </Form.Item>

          <Button
            onClick={onClose}
          >
            {translate('close')}
          </Button>
        </Space>
      </Form>
    </Card>
  )
}